import { zh } from '../i18n';

/**
 * Formatting helpers. Every returned 繁中 word comes from `../i18n`; this module
 * only decides which one and how the number is punctuated.
 */

const INTEGER = new Intl.NumberFormat('zh-Hant-TW');

export function formatInt(value: number): string {
  return INTEGER.format(Math.round(value));
}

/**
 * USD. Small amounts keep four decimals — a turn that cost $0.0031 must not be
 * rounded into a fake `$0.00` (SPEC §11 "no fake $0.00").
 */
export function formatUsd(value: number): string {
  const abs = Math.abs(value);
  const digits = abs >= 1 ? 2 : 4;
  return `$${value.toFixed(digits)}`;
}

export function formatBytes(bytes: number): string {
  if (bytes < 1024) return zh.units.bytes(formatInt(bytes));
  const kb = bytes / 1024;
  if (kb < 1024) return zh.units.kb(kb < 10 ? kb.toFixed(1) : formatInt(kb));
  const mb = kb / 1024;
  if (mb < 1024) return zh.units.mb(mb < 10 ? mb.toFixed(1) : formatInt(mb));
  return zh.units.gb((mb / 1024).toFixed(1));
}

export function formatMb(mb: number): string {
  return mb < 10 ? mb.toFixed(1) : formatInt(mb);
}

/** Seconds with one decimal below 10 s, whole seconds above. */
export function formatDurationSeconds(ms: number): string {
  const s = ms / 1000;
  return s < 10 ? s.toFixed(1) : formatInt(s);
}

/** `m:ss`, used by the approval countdown ring. */
export function formatCountdown(ms: number): string {
  const total = Math.max(0, Math.ceil(ms / 1000));
  const minutes = Math.floor(total / 60);
  const seconds = total % 60;
  return `${minutes}:${seconds.toString().padStart(2, '0')}`;
}

const DATE_TIME = new Intl.DateTimeFormat('zh-Hant-TW', {
  month: 'numeric',
  day: 'numeric',
  hour: '2-digit',
  minute: '2-digit',
  hour12: false,
});

export function formatDateTime(epochMs: number): string {
  return DATE_TIME.format(new Date(epochMs));
}

/** Relative for the last day, absolute beyond it — a thread list reads better. */
export function formatRelative(epochMs: number, now: number = Date.now()): string {
  const diff = now - epochMs;
  if (diff < 60_000) return zh.time.justNow;
  if (diff < 3_600_000) return zh.time.minutesAgo(Math.floor(diff / 60_000));
  if (diff < 86_400_000) return zh.time.hoursAgo(Math.floor(diff / 3_600_000));
  if (diff < 7 * 86_400_000) return zh.time.daysAgo(Math.floor(diff / 86_400_000));
  return formatDateTime(epochMs);
}

export function basename(path: string): string {
  const trimmed = path.replace(/\/+$/, '');
  const idx = trimmed.lastIndexOf('/');
  return idx === -1 ? trimmed : trimmed.slice(idx + 1);
}

export function dirname(path: string): string {
  const trimmed = path.replace(/\/+$/, '');
  const idx = trimmed.lastIndexOf('/');
  return idx <= 0 ? '' : trimmed.slice(0, idx);
}

export function shortSha(sha: string): string {
  return sha.slice(0, 7);
}
